// src/pages/admin/AdminAttributesScorer.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw, Target, Layers } from 'lucide-react';

const DEFAULTS = {
  motivationBoost: {
    enabled: true,
    requiredMultiplier: 100,
    preferredMultiplier: 70,
    unspecifiedMultiplier: 40,
  },
};

/* ── Toggle switch ───────────────────────────────────────── */
const Toggle = ({ checked, onChange, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, flexShrink: 0 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{
        position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 12,
        background: checked ? '#28A745' : '#ccc', transition: 'background 0.2s',
      }}>
        <span style={{
          position: 'absolute', height: 18, width: 18, left: checked ? 20 : 4, top: 3,
          background: '#fff', borderRadius: '50%', transition: 'left 0.2s',
        }} />
      </span>
    </label>
    {label && <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>}
  </div>
);

/* ── Score row ───────────────────────────────────────────── */
const ScoreRow = ({ label, value, onChange, suffix, description, max }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0' }}>
    <div style={{ flex: 1 }}>
      <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>
      {description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{description}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max={max || 999} value={value}
        onChange={e => onChange(Math.max(0, Math.min(max || 999, parseInt(e.target.value) || 0)))}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888', minWidth: 40 }}>{suffix || ''}</span>
    </div>
  </div>
);

/* ── Main page ───────────────────────────────────────────── */
export const AdminAttributesScorer = () => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mergeConfig = (d) => ({
    motivationBoost: { ...DEFAULTS.motivationBoost, ...d.motivationBoost },
  });

  useEffect(() => {
    matchingModelAPI.getAttributesConfig()
      .then(({ data }) => { if (data.data) setConfig(mergeConfig(data.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updateAttributesConfig(config);
      setConfig(mergeConfig(data.data));
      toast.success('Attributes scorer config saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.success('Reset to defaults');
  };

  const updateMotivation = (patch) =>
    setConfig(c => ({ ...c, motivationBoost: { ...c.motivationBoost, ...patch } }));

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  return (
    <div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Configure attribute matching between seeker profile attributes and ad position attribute requirements.
        Attributes are matched by exact name across 6 categories: Cognitive, Physical, Sensory, Interpersonal &amp; Emotional, Creative &amp; Perceptual, and Organisational &amp; Cognitive Style.
      </p>

      {/* ── Motivation Boost ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Target size={18} />
          <span>Employer Motivation</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            Attributes marked as Required by the employer carry more weight than Preferred or Unspecified.
          </p>

          <div style={{ marginBottom: 16 }}>
            <Toggle checked={config.motivationBoost.enabled} onChange={v => updateMotivation({ enabled: v })} label="Enable Motivation Weighting" />
          </div>

          <div style={{ opacity: config.motivationBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Required" value={config.motivationBoost.requiredMultiplier}
              onChange={v => updateMotivation({ requiredMultiplier: v })} suffix="%" max={200}
              description="Multiplier for attributes marked as Required." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Preferred" value={config.motivationBoost.preferredMultiplier}
              onChange={v => updateMotivation({ preferredMultiplier: v })} suffix="%" max={200}
              description="Multiplier for attributes marked as Preferred." />
            <div style={{ borderTop: '1px solid #eee' }} />
            <ScoreRow label="Unspecified" value={config.motivationBoost.unspecifiedMultiplier}
              onChange={v => updateMotivation({ unspecifiedMultiplier: v })} suffix="%" max={200}
              description="Multiplier for attributes with no motivation specified." />
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};
